import { useCallback, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { isAfter, parseISO, startOfDay } from "date-fns";
import { useIsMutating } from "@tanstack/react-query";
import {
    RadioInput,
    ActionButton,
    Button,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import {
    ConditionStatus,
    ConditionType,
    IEditEvaluationManagementNoteMedPayload,
    IGetClientNotes,
    ISessionNote,
    Modality,
    SessionLocations,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import { EVALUATION_MANAGEMENT_NOTE_MED_LABELS } from "../../../../utils/constants";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

import * as FilePicker from "../../../../shared-ui/FilePicker/FilePicker";
import * as Progress from "../../../../shared-ui/Progress/Progress";
import DeleteIcon from "../../../../icons/Delete";
import { IViewFile } from "../../../../utils/types";
import MedNoteEPrescribe from "../../../../modules/e-prescirbe/components/MedNoteEPrescribe/MedNoteEPrescribe";

interface EditEvaluationManagementNoteMedProps {
    noteDetails: ISessionNote;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

export default function EditEvaluationManagementNoteMed({
    noteDetails,
}: EditEvaluationManagementNoteMedProps) {
    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedNote, setSelectedNote] = useState({} as IGetClientNotes);
    const [docInView, setDocInView] = useState<IViewFile | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
    const [localDateOfNextSession, setLocalDateOfNextSession] =
        useState<Date | null>(
            noteDetails.note_content?.date_of_next_session
                ? parseISO(noteDetails.note_content.date_of_next_session)
                : null
        );

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const isSigning = useRef(false);

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const {
        handleAutoSaveNote,
        handleEditDraftNote,
        handleSignNote,
        handleAddNoteDocument,
        handleDeleteNoteContentDocument,
        uploadProgress,
    } = useEditNote();

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        watch,
        control,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditEvaluationManagementNoteMedPayload>({
        resolver: yupResolver(
            yup.object({
                diagnosis_codes: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .array()
                            .min(1, "Primary diagnosis is required"),
                        otherwise: yup.array(),
                    }),
                secondary_diagnosis: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                tertiary_diagnosis: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                additional_diagnosis_one: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                additional_diagnosis_two: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                interval_history: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                interval_psychiatric_assessment_mse: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                patient_ability_and_capability_to_respond_to_treatment: yup
                    .array()
                    .nullable(),
                diminished_poor_or_limited_quality_patient_oriented_evidence_details:
                    yup.string(),
                condition_type: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                condition_type_additional_details: yup.string(),
                condition_status: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                condition_status_additional_details: yup.string(),
                current_medications_and_medications_changes: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("This field is required"),
                        otherwise: yup.string(),
                    }),
                no_side_effects_or_adverse_reactions_noted_or_reported:
                    yup.bool(),
                lab_tests: yup.array().nullable(),
                lab_tests_details: yup.string(),
                additional_documentation_review_external: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("This field is required"),
                        otherwise: yup.string(),
                    }),
                independent_interpretation_of_tests: yup.string(),
                discussion_of_tests_or_management_with_external_professionals:
                    yup.string(),
                use_of_an_independent_historian: yup.string(),
                treatment_goal_one: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                treatment_goal_two: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                treatment_goal_three: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                progress_one: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                progress_two: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                progress_three: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                location_of_the_client: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Client location is required")
                            : field
                    ),
                other_location_of_the_client: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_client"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other client location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Provider location is required")
                            : field
                    ),
                other_location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_provider"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other provider location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                consent_was_received_for_the_teletherapy_session: yup.bool(),
                the_session_was_conducted_via_video: yup.bool(),
                session_location: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                modality: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                frequency_of_sessions: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1, "Frequency of sessions is required")
                        .required("Frequency of sessions is required"),
                    otherwise: yup.array().nullable(),
                }),
                other_frequency_of_sessions: yup.string(),
                date_of_next_session: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .date()
                        .required("Date of next session is required")
                        .test(
                            "is-after-dos",
                            "Date of next session must be after the current session date",
                            function validateDateAfterDOS(value) {
                                if (!value) return false;
                                if (!noteDetails.date_of_service) return true;
                                const dos = startOfDay(
                                    parseISO(noteDetails.date_of_service)
                                );
                                const nextSession = startOfDay(value);
                                return isAfter(nextSession, dos);
                            }
                        ),
                    otherwise: yup.date().nullable(),
                }),
                communication_factors_impacting_treatment: yup
                    .array()
                    .nullable(),
                details_around_communication_factors_impacting_treatment:
                    yup.string(),
                prognosis: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const isAddDocMutating = useIsMutating(["add-note-content-document"]);
    const isDeleteDocMutating = useIsMutating(["delete-note-content-document"]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else {
                setSelectedFiles([...acceptedFiles]);
            }
        },
        [selectedFiles]
    );

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue(
            "location_of_the_client",
            note.note_content.location_of_the_client
        );
        setValue(
            "other_location_of_the_client",
            note.note_content.other_location_of_the_client
        );
        setValue(
            "location_of_the_provider",
            note.note_content.location_of_the_provider
        );
        setValue(
            "other_location_of_the_provider",
            note.note_content.other_location_of_the_provider
        );
        setValue(
            "consent_was_received_for_the_teletherapy_session",
            note.note_content.consent_was_received_for_the_teletherapy_session
        );
        setValue(
            "the_session_was_conducted_via_video",
            note.note_content.the_session_was_conducted_via_video
        );

        const dateOfNextSession =
            note.note_content.date_of_next_session ?? null;
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue(
            "secondary_diagnosis",
            note.note_content.secondary_diagnosis
                ? [note.note_content.secondary_diagnosis]
                : undefined
        );
        setValue(
            "tertiary_diagnosis",
            note.note_content.tertiary_diagnosis
                ? [note.note_content.tertiary_diagnosis]
                : undefined
        );
        setValue(
            "additional_diagnosis_one",
            note.note_content.additional_diagnosis_one
                ? [note.note_content.additional_diagnosis_one]
                : undefined
        );
        setValue(
            "additional_diagnosis_two",
            note.note_content.additional_diagnosis_two
                ? [note.note_content.additional_diagnosis_two]
                : undefined
        );
        setValue("interval_history", note.note_content?.interval_history);
        setValue(
            "date_of_next_session",
            note.note_content?.date_of_next_session
        );
        setLocalDateOfNextSession(
            dateOfNextSession ? parseISO(dateOfNextSession) : null
        );
        setValue(
            "interval_psychiatric_assessment_mse",
            note.note_content.interval_psychiatric_assessment_mse
        );
        setValue(
            "patient_ability_and_capability_to_respond_to_treatment",
            note.note_content
                .patient_ability_and_capability_to_respond_to_treatment
        );
        setValue(
            "diminished_poor_or_limited_quality_patient_oriented_evidence_details",
            note.note_content
                .diminished_poor_or_limited_quality_patient_oriented_evidence_details
        );
        setValue("condition_type", note.note_content.condition_type);
        setValue(
            "condition_type_additional_details",
            note.note_content.condition_type_additional_details
        );
        setValue("condition_status", note.note_content.condition_status);
        setValue(
            "condition_status_additional_details",
            note.note_content.condition_status_additional_details
        );
        setValue(
            "current_medications_and_medications_changes",
            note.note_content.current_medications_and_medications_changes
        );
        setValue(
            "no_side_effects_or_adverse_reactions_noted_or_reported",
            note.note_content
                .no_side_effects_or_adverse_reactions_noted_or_reported
        );
        setValue("lab_tests", note.note_content.lab_tests);
        setValue("lab_tests_details", note.note_content.lab_tests_details);
        setValue(
            "additional_documentation_review_external",
            note.note_content.additional_documentation_review_external
        );
        setValue(
            "independent_interpretation_of_tests",
            note.note_content.independent_interpretation_of_tests
        );
        setValue(
            "discussion_of_tests_or_management_with_external_professionals",
            note.note_content
                .discussion_of_tests_or_management_with_external_professionals
        );
        setValue(
            "use_of_an_independent_historian",
            note.note_content.use_of_an_independent_historian
        );
        setValue("treatment_goal_one", note.note_content.treatment_goal_one);
        setValue("treatment_goal_two", note.note_content.treatment_goal_two);
        setValue(
            "treatment_goal_three",
            note.note_content.treatment_goal_three
        );
        setValue("progress_one", note.note_content.progress_one);
        setValue("progress_two", note.note_content.progress_two);
        setValue("progress_three", note.note_content.progress_three);
        setValue("session_location", note.note_content.session_location);
        setValue(
            "frequency_of_sessions",
            note.note_content.frequency_of_sessions
        );
        setValue(
            "other_frequency_of_sessions",
            note.note_content.other_frequency_of_sessions
        );
        setValue(
            "communication_factors_impacting_treatment",
            note.note_content.communication_factors_impacting_treatment
        );
        setValue(
            "details_around_communication_factors_impacting_treatment",
            note.note_content
                .details_around_communication_factors_impacting_treatment
        );
        setValue("prognosis", note.note_content.prognosis);
        setValue("modality", note.note_content.modality);
        setShowPrompt(false);
    };

    const handleIsChecked = (checkboxValue: string, values?: string[]) => {
        return Boolean(
            values?.find(
                (value: string) => value.trim() === checkboxValue.trim()
            )
        );
    };

    const conditionTypeOptionsForSelect = Object.keys(ConditionType).map(
        (conditionType) => ({
            label: removeEnumUnderscore(conditionType),
            value: ConditionType[conditionType as keyof typeof ConditionType],
        })
    );

    const conditionStatusOptionsForSelect = Object.keys(ConditionStatus).map(
        (conditionStatus) => ({
            label: removeEnumUnderscore(conditionStatus),
            value: ConditionStatus[
                conditionStatus as keyof typeof ConditionStatus
            ],
        })
    );

    const sessionLocationOptionsForSelect = Object.keys(SessionLocations).map(
        (location) => ({
            label: removeEnumUnderscore(location),
            value: SessionLocations[location as keyof typeof SessionLocations],
        })
    );

    const modalityOptionsForSelect = Object.keys(Modality).map((modality) => ({
        label: removeEnumUnderscore(modality),
        value: Modality[modality as keyof typeof Modality],
    }));

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />

            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}

            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="evaluation management note form"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <Controller
                        name="session_location"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.session_location
                                }
                                placeholder=""
                                options={sessionLocationOptionsForSelect}
                                onChange={(val) =>
                                    field.onChange(
                                        (val as { value: string }).value
                                    )
                                }
                                value={
                                    sessionLocationOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                hasError={!!errors.session_location}
                                errorText={errors.session_location?.message}
                            />
                        )}
                    />
                </div>
                {watch("session_location") === SessionLocations.Telehealth && (
                    <>
                        <h3 className="note-label-req">
                            {
                                EVALUATION_MANAGEMENT_NOTE_MED_LABELS.location_of_the_client
                            }
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_client")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_client === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_client ? (
                            <div className="note-error">
                                <p>{errors.location_of_the_client?.message}</p>
                            </div>
                        ) : null}
                        {watch("location_of_the_client") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_client"
                                    )}
                                    label={
                                        EVALUATION_MANAGEMENT_NOTE_MED_LABELS.other_location_of_the_client
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_client
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_client
                                    }
                                    errorText={
                                        errors.other_location_of_the_client
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <h3 className="note-label-req">
                            {
                                EVALUATION_MANAGEMENT_NOTE_MED_LABELS.location_of_the_provider
                            }
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_provider")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_provider === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_provider ? (
                            <div className="note-error">
                                <p>
                                    {errors.location_of_the_provider?.message}
                                </p>
                            </div>
                        ) : null}
                        {watch("location_of_the_provider") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_provider"
                                    )}
                                    label={
                                        EVALUATION_MANAGEMENT_NOTE_MED_LABELS.other_location_of_the_provider
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_provider
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_provider
                                    }
                                    errorText={
                                        errors.other_location_of_the_provider
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <div className="note-checkboxes">
                            <FormCheckbox
                                {...register(
                                    "consent_was_received_for_the_teletherapy_session"
                                )}
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.consent_was_received_for_the_teletherapy_session
                                }
                            />
                            <FormCheckbox
                                {...register(
                                    "the_session_was_conducted_via_video"
                                )}
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.the_session_was_conducted_via_video
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.the_session_was_conducted_via_video
                                }
                            />
                        </div>
                    </>
                )}

                <div className="fg">
                    <Controller
                        name="diagnosis_codes"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.diagnosis_codes
                                }
                                value={field.value}
                                onChange={field.onChange}
                                isRequired
                                errorText={
                                    errors.diagnosis_codes?.type === "typeError"
                                        ? "Diagnosis codes are required"
                                        : errors.diagnosis_codes?.message
                                }
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="secondary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.secondary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.secondary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.tertiary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.tertiary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_one"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.additional_diagnosis_one
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_one?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_two"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.additional_diagnosis_two
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_two?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <MedNoteEPrescribe
                    noteDetails={noteDetails}
                    showDiagnosisSync
                />
                <div className="fg">
                    <TextArea
                        {...register("interval_history")}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.interval_history
                        }
                        hasError={Boolean(errors.interval_history)}
                        errorText={errors.interval_history?.message}
                        defaultValue={
                            noteDetails.note_content?.interval_history
                        }
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("interval_psychiatric_assessment_mse")}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.interval_psychiatric_assessment_mse
                        }
                        hasError={Boolean(
                            errors.interval_psychiatric_assessment_mse
                        )}
                        errorText={
                            errors.interval_psychiatric_assessment_mse?.message
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.interval_psychiatric_assessment_mse
                        }
                        isRequired
                    />
                </div>
                <div>
                    <h3 className="note-label note-label-opt">
                        {
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.patient_ability_and_capability_to_respond_to_treatment
                        }
                    </h3>
                    <div className="note-checkboxes">
                        {[
                            "Consistent, good-quality patient-oriented evidence",
                            "Diminished/poor or limited-quality patient-oriented evidence",
                        ].map((value) => (
                            <FormCheckbox
                                key={value}
                                {...register(
                                    "patient_ability_and_capability_to_respond_to_treatment"
                                )}
                                label={value}
                                value={value}
                                defaultChecked={handleIsChecked(
                                    value,
                                    noteDetails.note_content
                                        .patient_ability_and_capability_to_respond_to_treatment
                                )}
                            />
                        ))}
                    </div>
                </div>
                <div className="fg">
                    <TextArea
                        {...register(
                            "diminished_poor_or_limited_quality_patient_oriented_evidence_details"
                        )}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.diminished_poor_or_limited_quality_patient_oriented_evidence_details
                        }
                        hasError={Boolean(
                            errors.diminished_poor_or_limited_quality_patient_oriented_evidence_details
                        )}
                        errorText={
                            errors
                                .diminished_poor_or_limited_quality_patient_oriented_evidence_details
                                ?.message
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.diminished_poor_or_limited_quality_patient_oriented_evidence_details
                        }
                        isOptional
                    />
                </div>
                <h3 className="note-header">
                    {EVALUATION_MANAGEMENT_NOTE_MED_LABELS.prognosis}
                </h3>
                <p className="note-label-req">
                    Please write at least 1-2 sentences about the likely
                    outcome/development of the symptoms/challenges
                </p>
                <div className="fg">
                    <TextArea
                        {...register("prognosis")}
                        defaultValue={noteDetails.note_content?.prognosis}
                        hasError={!!errors.prognosis}
                        errorText={errors.prognosis?.message}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="condition_type"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.condition_type
                                }
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    conditionTypeOptionsForSelect.find(
                                        (type) => type.value === field.value
                                    ) || null
                                }
                                options={conditionTypeOptionsForSelect}
                                hasError={!!errors.condition_type}
                                errorText={errors.condition_type?.message}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("condition_type_additional_details")}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.condition_type_additional_details
                        }
                        errorText={
                            errors.condition_type_additional_details?.message
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.condition_type_additional_details
                        }
                        hasError={!!errors.condition_type_additional_details}
                        isOptional
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="condition_status"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.condition_status
                                }
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    conditionStatusOptionsForSelect.find(
                                        (status) => status.value === field.value
                                    ) || null
                                }
                                options={conditionStatusOptionsForSelect}
                                hasError={!!errors.condition_status}
                                errorText={errors.condition_status?.message}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("condition_status_additional_details")}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.condition_status_additional_details
                        }
                        errorText={
                            errors.condition_status_additional_details?.message
                        }
                        hasError={!!errors.condition_status_additional_details}
                        defaultValue={
                            noteDetails.note_content
                                ?.condition_status_additional_details
                        }
                        isOptional
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register(
                            "current_medications_and_medications_changes"
                        )}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.current_medications_and_medications_changes
                        }
                        hasError={Boolean(
                            errors.current_medications_and_medications_changes
                        )}
                        errorText={
                            errors.current_medications_and_medications_changes
                                ?.message
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.current_medications_and_medications_changes
                        }
                        isRequired
                    />
                </div>
                <div className="fg">
                    <FormCheckbox
                        {...register(
                            "no_side_effects_or_adverse_reactions_noted_or_reported"
                        )}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.no_side_effects_or_adverse_reactions_noted_or_reported
                        }
                        defaultChecked={
                            noteDetails.note_content
                                ?.no_side_effects_or_adverse_reactions_noted_or_reported
                        }
                    />
                </div>
                <h3 className="note-label note-label-opt">
                    {EVALUATION_MANAGEMENT_NOTE_MED_LABELS.lab_tests}
                </h3>
                <div className="note-checkboxes">
                    {["Ordered", "Reviewed"].map((value) => (
                        <FormCheckbox
                            key={value}
                            {...register("lab_tests")}
                            label={value}
                            value={value}
                            defaultChecked={handleIsChecked(
                                value,
                                noteDetails.note_content.lab_tests
                            )}
                        />
                    ))}
                </div>
                {errors.lab_tests ? (
                    <div className="note-error !mt-0">
                        <p>Please select at least one option</p>
                    </div>
                ) : null}
                <div className="fg">
                    <TextArea
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.lab_tests_details
                        }
                        {...register("lab_tests_details")}
                        defaultValue={
                            noteDetails.note_content?.lab_tests_details
                        }
                        hasError={Boolean(errors.lab_tests_details)}
                        errorText={errors.lab_tests_details?.message}
                        isOptional
                    />
                </div>
                <div className="fg">
                    <TextArea
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.additional_documentation_review_external
                        }
                        {...register(
                            "additional_documentation_review_external"
                        )}
                        defaultValue={
                            noteDetails.note_content
                                ?.additional_documentation_review_external
                        }
                        hasError={Boolean(
                            errors.additional_documentation_review_external
                        )}
                        errorText={
                            errors.additional_documentation_review_external
                                ?.message
                        }
                        isRequired
                    />
                </div>
                <div className="fg">
                    <TextArea
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.independent_interpretation_of_tests
                        }
                        {...register("independent_interpretation_of_tests")}
                        defaultValue={
                            noteDetails.note_content
                                ?.independent_interpretation_of_tests
                        }
                        isOptional
                    />
                </div>
                <div className="fg">
                    <TextArea
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.discussion_of_tests_or_management_with_external_professionals
                        }
                        {...register(
                            "discussion_of_tests_or_management_with_external_professionals"
                        )}
                        defaultValue={
                            noteDetails.note_content
                                ?.discussion_of_tests_or_management_with_external_professionals
                        }
                        isOptional
                    />
                </div>
                <div className="fg">
                    <TextArea
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.use_of_an_independent_historian
                        }
                        {...register("use_of_an_independent_historian")}
                        defaultValue={
                            noteDetails.note_content
                                ?.use_of_an_independent_historian
                        }
                        isOptional
                    />
                </div>
                <div className="flex flex-col gap-y-[--spacing-form-items] mb-[--spacing-form-items]">
                    <TextArea
                        {...register("treatment_goal_one")}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.treatment_goal_one
                        }
                        defaultValue={
                            noteDetails.note_content?.treatment_goal_one
                        }
                        isRequired
                        hasError={!!errors.treatment_goal_one}
                        errorText={errors.treatment_goal_one?.message}
                    />
                    <TextArea
                        {...register("treatment_goal_two")}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.treatment_goal_two
                        }
                        defaultValue={
                            noteDetails.note_content?.treatment_goal_two
                        }
                        isRequired
                        hasError={!!errors.treatment_goal_two}
                        errorText={errors.treatment_goal_two?.message}
                    />
                    <TextArea
                        {...register("treatment_goal_three")}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.treatment_goal_three
                        }
                        defaultValue={
                            noteDetails.note_content?.treatment_goal_three
                        }
                        isRequired
                        hasError={!!errors.treatment_goal_three}
                        errorText={errors.treatment_goal_three?.message}
                    />
                </div>
                <div className="flex flex-col gap-y-[--spacing-form-items] mb-[--spacing-form-items]">
                    <TextArea
                        {...register("progress_one")}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.progress_one
                        }
                        defaultValue={noteDetails.note_content?.progress_one}
                        isRequired
                        hasError={!!errors.progress_one}
                        errorText={errors.progress_one?.message}
                    />
                    <TextArea
                        {...register("progress_two")}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.progress_two
                        }
                        defaultValue={noteDetails.note_content?.progress_two}
                        isRequired
                        hasError={!!errors.progress_two}
                        errorText={errors.progress_two?.message}
                    />
                    <TextArea
                        {...register("progress_three")}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.progress_three
                        }
                        defaultValue={noteDetails.note_content?.progress_three}
                        isRequired
                        hasError={!!errors.progress_three}
                        errorText={errors.progress_three?.message}
                    />
                </div>

                <div className="fg">
                    <Controller
                        name="modality"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.modality
                                }
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    modalityOptionsForSelect.find(
                                        (location) =>
                                            location.value === field.value
                                    ) || null
                                }
                                options={modalityOptionsForSelect}
                                hasError={!!errors.modality}
                                errorText={errors.modality?.message}
                            />
                        )}
                    />
                </div>

                <h3 className="note-label-req">
                    {
                        EVALUATION_MANAGEMENT_NOTE_MED_LABELS.frequency_of_sessions
                    }
                </h3>
                <div className="note-checkboxes">
                    {["Weekly", "Bi-Weekly", "Monthly", "Other"].map(
                        (value) => (
                            <FormCheckbox
                                key={value}
                                {...register("frequency_of_sessions")}
                                label={value}
                                value={value}
                                defaultChecked={handleIsChecked(
                                    value,
                                    noteDetails?.note_content
                                        ?.frequency_of_sessions || []
                                )}
                            />
                        )
                    )}
                </div>
                {errors.frequency_of_sessions ? (
                    <div className="note-error">
                        <p>{errors.frequency_of_sessions.message}</p>
                    </div>
                ) : null}
                <div className="fg">
                    <Input
                        {...register("other_frequency_of_sessions")}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.other_frequency_of_sessions
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.other_frequency_of_sessions
                        }
                        isOptional
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="date_of_next_session"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                isRequired
                                label={
                                    EVALUATION_MANAGEMENT_NOTE_MED_LABELS.date_of_next_session
                                }
                                onChange={(date) => {
                                    field.onChange(date ?? null);
                                    setLocalDateOfNextSession(date);
                                }}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                selected={localDateOfNextSession}
                                hasError={Boolean(errors.date_of_next_session)}
                                errorText="This field is required"
                            />
                        )}
                    />
                </div>
                <h3 className="note-label note-label-opt">
                    {
                        EVALUATION_MANAGEMENT_NOTE_MED_LABELS.communication_factors_impacting_treatment
                    }
                </h3>
                <div className="note-checkboxes">
                    {[
                        "Need to manage maladaptive communication (e.g., related to high anxiety, high reactivity, repeated questions, or disagreement) among participants",
                        "Caregiver emotions or behaviors interfered with the caregiver's understanding and ability to assist in the implementation of the treatment plan",
                        "Evidence or disclosure of a sentinel event and mandated reporting to a third party",
                        "Use of play equipment or other physical devices to communicate with the client to overcome barriers to therapeutic or diagnostic interaction",
                        "Client has not developed, or lost, either the expressive language communication skills to explain their symptoms and respond to treatment",
                        "Client lacks the receptive communication skills to understand the therapist when using typical language for communication",
                    ].map((value) => (
                        <FormCheckbox
                            key={value}
                            {...register(
                                "communication_factors_impacting_treatment"
                            )}
                            label={value}
                            value={value}
                            defaultChecked={handleIsChecked(
                                value,
                                noteDetails.note_content
                                    .communication_factors_impacting_treatment
                            )}
                        />
                    ))}
                </div>
                {errors.communication_factors_impacting_treatment ? (
                    <div className="note-error !mt-0">
                        <p>Please select at least one option</p>
                    </div>
                ) : null}
                <div className="fg">
                    <TextArea
                        {...register(
                            "details_around_communication_factors_impacting_treatment"
                        )}
                        label={
                            EVALUATION_MANAGEMENT_NOTE_MED_LABELS.details_around_communication_factors_impacting_treatment
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.details_around_communication_factors_impacting_treatment
                        }
                        isOptional
                        hasError={
                            !!errors.details_around_communication_factors_impacting_treatment
                        }
                        errorText={
                            errors
                                .details_around_communication_factors_impacting_treatment
                                ?.message
                        }
                    />
                </div>
                <h3 className="note-label-opt">
                    {EVALUATION_MANAGEMENT_NOTE_MED_LABELS.files}
                </h3>
                <div className="fg flex flex-col gap-y-[--spacing-form-items]">
                    <FilePicker.FilePicker
                        onDrop={onDrop}
                        disabled={isAddDocMutating > 0}
                    >
                        <FilePicker.FilePickerIcon />
                        <FilePicker.FilePickerMeta description="Click to upload note files" />
                    </FilePicker.FilePicker>
                    {selectedFiles && selectedFiles.length > 0 ? (
                        <div className="flex flex-col gap-y-[--spacing-items]">
                            <FilePicker.FilePickerSelectedFiles
                                selectedFiles={selectedFiles}
                                handleRemoveSelectedFile={
                                    handleRemoveSelectedFile
                                }
                                wrapperClassName="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] grid-flow-row flex-row gap-[--spacing-items]"
                            />
                            <Button
                                className="self-start"
                                onClick={() =>
                                    handleAddNoteDocument(selectedFiles, () =>
                                        setSelectedFiles(null)
                                    )
                                }
                                disabled={isAddDocMutating > 0}
                            >
                                Save files
                            </Button>
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <Progress.Root value={uploadProgress}>
                                    <Progress.Label>
                                        Uploading {selectedFiles.length}{" "}
                                        {selectedFiles.length === 1
                                            ? "file"
                                            : "files"}
                                        ...
                                    </Progress.Label>
                                    <Progress.Value />
                                    <Progress.Track>
                                        <Progress.Indicator />
                                    </Progress.Track>
                                </Progress.Root>
                            )}
                        </div>
                    ) : null}
                    {noteDetails?.note_content_documents &&
                    noteDetails?.note_content_documents.length > 0 ? (
                        <div className="flex flex-col gap-y-[var(--spacing-items)]">
                            {noteDetails.note_content_documents.map((doc) => (
                                <div
                                    key={doc.note_content_document_id}
                                    className="flex items-center gap-x-3"
                                >
                                    <ActionButton
                                        variant="can-hover"
                                        size="auto"
                                        className="h-7 !text-text text-caption underline"
                                        actionTitle="View file"
                                        onClick={() => {
                                            setDocInView(doc);
                                        }}
                                    >
                                        <span>{`${displayNameInRightFormat(
                                            {
                                                firstName:
                                                    doc.uploaded_by.first_name,
                                                lastName:
                                                    doc.uploaded_by.last_name,
                                            },
                                            false
                                        )} -  ${doc.document_name}`}</span>
                                    </ActionButton>
                                    <ActionButton
                                        variant="can-hover"
                                        aria-label="delete icon"
                                        size="auto"
                                        className="h-7"
                                        onClick={() =>
                                            handleDeleteNoteContentDocument(
                                                doc.note_content_document_id
                                            )
                                        }
                                        disabled={isDeleteDocMutating > 0}
                                        actionTitle="Delete file from note content"
                                    >
                                        <DeleteIcon />
                                    </ActionButton>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={Boolean(errors.signature)}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </>
    );
}
